import {
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import ButtonWithSpinner from '../../../../components/buttons/ButtonWithSpinner';
import DateFromToForm from '../../../../components/form/DateFromToForm';
import MultiSelector from '../../../../components/selector/MultiSelector';
import CollapsibleGroupMultiSelector from '../../../../components/selector/CollapsibleGroupMultiSelector';
import ReportTable from '../../../../components/table/ReportTable';
import { useClassRoomReport, useTableStateManagement, createRenderer, ALL_SELECTOR } from './useClassRoomReport';
import { useStudentGroups } from './useStudentGroups';
import { useReportAssignments } from './useReportAssignments';
import { dateFormatMMddyyyy } from '../../../../AppConstants';
import { URL_MY_STUDENT } from '../../../../routes/RouteConstants';
import './ClassroomReport.scss';
import { useUserLink } from '../../MyClassRoomUtils';
// import { MY_CLASSROOM_ROUTES } from '../../MyClassRoomConstant';
import { useGetSubjectsByUserComponents } from '../../../hooks/SubjectsByUsersHook';

const fileName = 'VizZle NextGen';
const pdfTitle = 'Class Summary';

const ClassroomReport = () => {
  // Fetched here for the dropdown and injected into useClassRoomReport so that
  // loadClassReport can resolve the selected group ids into their student ids.
  const { studentGroups, studentGroupOptions, loading: studentGroupsLoading } = useStudentGroups();

  const {
    loading,
    hasLoaded,
    selectedCriteria,
    validDates,
    tableData,
    handleOnDateChange,
    handleOnSelectorChange,
    handleOnActivitiesChange,
    handleOnSubmitButtonClick,
  } = useClassRoomReport({ studentGroups });

  const {
    tableState,
    handleOnTableStateChange,
  } = useTableStateManagement();

  const goToMyStudent = useUserLink({
    pathName: URL_MY_STUDENT,
    // goBackToPathName: `${URL_MY_CLASSROOM}/${MY_CLASSROOM_ROUTES.CLASS_REPORT}`,
    // label: 'Back To Classroom',
  });

  const tableConfig = [
    {
      title: 'Username',
      align: 'left',
      dataField: 'userName',
      width: '15%',
      skipDataRenderForReport: true,
      dataRenderer: (data) => {
        const { userName } = data;
        return (
          <Typography
            tabIndex={0}
            className='user-name-link'
            data-dd-privacy='mask'
            onClick={() => { goToMyStudent(data); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToMyStudent(data);
              }
            }}
            component='a'
          >
            {userName}
          </Typography>
        );
      },
    },
    {
      title: 'First Name',
      align: 'left',
      dataField: 'firstName',
      width: '15%',
      skipDataRenderForReport: true,
      dataRenderer: (data) => (
        <Typography data-dd-privacy='mask'>{data.firstName}</Typography>
      ),
    },
    {
      title: 'Last Name',
      align: 'left',
      dataField: 'lastName',
      width: '15%',
      skipDataRenderForReport: true,
      dataRenderer: (data) => (
        <Typography data-dd-privacy='mask'>{data.lastName}</Typography>
      ),
    },
    {
      title: 'Total Activities',
      align: 'center',
      dataField: 'totalLessons',
      width: '13%',
    },
    {
      title: 'Questions Answered',
      align: 'center',
      dataField: 'totalQuestionsAnswered',
      width: '15%',
    },
    {
      title: 'Percentage Correct',
      align: 'center',
      dataField: 'correctPercentage',
      width: '15%',
      dataRenderer: (data) => {
        if (data.totalQuestionsAnswered > 0) {
          return data.correctPercentage > 0 ? data.correctPercentage.toString() : '0';
        } else {
          return data.correctPercentage > 0 ? data.correctPercentage.toString() : 'N/A';
        }
      },
    },
    {
      title: 'Total Time (Min)',
      align: 'center',
      dataField: 'totalTimeInMinutes',
      width: '16%',
    },
  ];

  const subjects = useGetSubjectsByUserComponents();
  subjects.unshift(ALL_SELECTOR);

  // Resolve concrete subject ids for the assignments fetch (exclude "All").
  // Activities can load for one or more selected subjects.
  const concreteSubjects = (selectedCriteria.subjects || [])
    .filter((s) => s !== ALL_SELECTOR.value);

  const { assignmentOptions, loading: assignmentsLoading } = useReportAssignments({
    startDate: selectedCriteria.startDate,
    endDate: selectedCriteria.endDate,
    subjects: concreteSubjects,
  });

  // Prepend an "All" option so the Student Group dropdown mirrors the Subject filter.
  const studentGroupItems = [ALL_SELECTOR, ...studentGroupOptions];

  // Activities requires at least one concrete subject (not "All").
  const isActivitiesDisabled = concreteSubjects.length === 0;

  const displayTable = () => (
    hasLoaded ? (
      <ReportTable
        config={tableConfig}
        tableData={tableData}
        fileName={fileName}
        title={pdfTitle}
        loading={loading}
        onTableStateChange={handleOnTableStateChange}
        initTableState={tableState}
      />
    ) : <div className='search-text'>Please perform a search to retrieve data for your class</div>
  );

  return (
    <div className='classroom-report'>
      <DateFromToForm
        startDate={selectedCriteria.startDate}
        endDate={selectedCriteria.endDate}
        dateFormat={dateFormatMMddyyyy}
        onChange={handleOnDateChange}
      >
        <div className='additional-criteria'>
          <FormControl className='criteria-container'>
            <InputLabel className='criteria-label' htmlFor='detailed-report-subject'>Subject</InputLabel>
            <MultiSelector
              inputProps={{
                name: 'subject-title',
                id: 'detailed-report-subject',
                'aria-label': 'Subject',
              }}
              className='criterial-selector'
              selectorItems={subjects}
              onChange={(event) => { handleOnSelectorChange(event.target.value, 'subjects', subjects[0].value); }}
              values={selectedCriteria.subjects}
              renderValue={(selected) => createRenderer(selected, subjects)}
              variant='filled'
              data-test='subject-selector'
              MenuProps={{ disableAutoFocusItem: true }}
            />
          </FormControl>
          <div className='criteria-container activities-selector-container'>
            <CollapsibleGroupMultiSelector
              dataTestId='activities'
              label='Activities'
              placeholder='Select Subject'
              className='activities-selector'
              options={assignmentOptions}
              loading={assignmentsLoading}
              disabled={isActivitiesDisabled}
              initFilterKeyValues={selectedCriteria.activities || []}
              onChange={(selected) => { handleOnActivitiesChange(selected.map((o) => o.key)); }}
            />
          </div>
          <FormControl className='criteria-container'>
            <InputLabel className='criteria-label' htmlFor='detailed-report-student-group'>Student Group</InputLabel>
            <MultiSelector
              inputProps={{
                name: 'student-group-title',
                id: 'detailed-report-student-group',
                'aria-label': 'Student Group',
              }}
              className='criterial-selector'
              selectorItems={studentGroupItems}
              disabled={studentGroupsLoading}
              autoWidth={false}
              onChange={(event) => { handleOnSelectorChange(event.target.value, 'studentGroups', ALL_SELECTOR.value); }}
              values={selectedCriteria.studentGroups || [ALL_SELECTOR.value]}
              renderValue={(selected) => createRenderer(selected, studentGroupItems)}
              variant='filled'
              data-test='student-group-selector'
              MenuProps={{ disableAutoFocusItem: true }}
            />
          </FormControl>
        </div>
        <ButtonWithSpinner
          className='submit-button'
          label='Submit'
          loading={loading}
          onClick={handleOnSubmitButtonClick}
          disabled={!validDates}
          data-test='submit-button'
        />
      </DateFromToForm>

      <div className='data-table'>
        {displayTable()}
      </div>
    </div>
  );
};

export default ClassroomReport;
